import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsApi, quotesApi } from '../lib/api';
import { formatCurrency, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
    ArrowLeft, 
    Save, 
    Search, 
    Plus, 
    Minus, 
    Trash2,
    CalendarIcon,
    User,
    MapPin,
    PartyPopper,
    TrendingUp,
    Euro,
    Percent,
    Package,
    Leaf,
    AlertCircle,
    ChevronDown
} from 'lucide-react';

const CATEGORIES = [
    { value: 'all', label: 'Tous' },
    { value: 'sale', label: 'Salé' },
    { value: 'sucre', label: 'Sucré' },
    { value: 'boisson', label: 'Boisson' },
];

// Component for grouped product with variants
function GroupedProductCard({ title, variants, onSelectVariant }) {
    const [isOpen, setIsOpen] = useState(false);
    
    const hasMultipleVariants = variants.length > 1;
    const firstVariant = variants[0];
    
    const handleClick = () => {
        if (hasMultipleVariants) {
            setIsOpen(!isOpen);
        } else {
            onSelectVariant(firstVariant);
        }
    };
    
    // Get combined badges from all variants
    const getBadges = (product) => {
        const badges = [];
        if (product.is_halal) badges.push({ type: 'H', color: 'bg-emerald-600' });
        if (product.is_vegan) badges.push({ type: 'V', color: 'bg-green-500' });
        if (product.contains_pork) badges.push({ type: 'P', color: 'bg-red-500' });
        return badges;
    };
    
    return (
        <div className="relative">
            <div
                onClick={handleClick}
                className={`p-3 rounded-lg bg-[#020617]/50 border border-[#1e293b] hover:border-[#d4af37]/50 cursor-pointer transition-all ${isOpen ? 'border-[#d4af37] bg-[#0f172a]' : ''}`}
                data-testid={`grouped-product-${title}`}
            >
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="text-[#f8fafc] font-medium truncate">{title}</p>
                            {hasMultipleVariants && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-[#d4af37]/20 text-[#d4af37]">
                                    {variants.length}
                                </span>
                            )}
                        </div>
                        <p className="text-[#64748b] text-xs">
                            {hasMultipleVariants ? `${variants.length} déclinaisons` : getCategoryLabel(firstVariant.category)}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                        {!hasMultipleVariants && getBadges(firstVariant).map((badge, i) => (
                            <span key={i} className={`${badge.color} w-5 h-5 rounded flex items-center justify-center text-xs text-white font-bold`}>
                                {badge.type}
                            </span>
                        ))}
                        {hasMultipleVariants && (
                            <ChevronDown className={`w-4 h-4 text-[#64748b] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        )}
                    </div>
                </div>
                {!hasMultipleVariants && (
                    <p className="text-[#d4af37] font-semibold mt-2 mono text-sm">
                        {formatCurrency(firstVariant.unit_price)}
                    </p>
                )}
                {hasMultipleVariants && !isOpen && (
                    <p className="text-[#94a3b8] text-xs mt-2">
                        À partir de {formatCurrency(Math.min(...variants.map(v => v.unit_price)))}
                    </p>
                )}
            </div>
            
            {/* Dropdown for variants */}
            {hasMultipleVariants && isOpen && (
                <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg bg-[#0f172a] border border-[#1e293b] shadow-xl overflow-hidden">
                    {variants.map((variant) => (
                        <div
                            key={variant.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectVariant(variant);
                                setIsOpen(false);
                            }}
                            className="p-3 hover:bg-[#1e293b] cursor-pointer border-b border-[#1e293b] last:border-b-0 transition-colors"
                            data-testid={`variant-${variant.id}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[#f8fafc] text-sm font-medium">
                                        {variant.description || variant.title}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                        {getBadges(variant).map((badge, i) => (
                                            <span key={i} className={`${badge.color} px-1.5 py-0.5 rounded text-xs text-white font-bold`}>
                                                {badge.type}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-[#d4af37] font-semibold mono text-sm ml-3">
                                    {formatCurrency(variant.unit_price)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function QuoteEditorPage() {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(quoteId);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');

    const [clientInfo, setClientInfo] = useState({
        client_name: '',
        client_address: '',
        event_type: '',
        event_date: null,
    });

    const [quoteItems, setQuoteItems] = useState([]);

    useEffect(() => {
        loadData();
    }, [quoteId]);

    const loadData = async () => {
        try {
            const productsRes = await productsApi.getAll();
            setProducts(productsRes.data);

            if (quoteId) {
                const quoteRes = await quotesApi.getOne(quoteId);
                const quote = quoteRes.data;
                setClientInfo({
                    client_name: quote.client_name,
                    client_address: quote.client_address,
                    event_type: quote.event_type,
                    event_date: quote.event_date ? new Date(quote.event_date) : null,
                });
                setQuoteItems(quote.items);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    // Group products by title
    const groupedProducts = useMemo(() => {
        const filtered = products.filter((product) => {
            const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
            return matchesSearch && matchesCategory;
        });
        
        const groups = {};
        filtered.forEach(product => {
            const key = product.title;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(product);
        });
        
        return Object.entries(groups).map(([title, variants]) => ({
            title,
            variants: variants.sort((a, b) => a.unit_price - b.unit_price)
        }));
    }, [products, searchQuery, activeCategory]);

    const addProduct = (product) => {
        const existing = quoteItems.find(item => item.product_id === product.id);
        if (existing) {
            setQuoteItems(quoteItems.map(item =>
                item.product_id === product.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            ));
        } else {
            setQuoteItems([...quoteItems, {
                product_id: product.id,
                title: product.title,
                description: product.description || '',
                unit_price: product.unit_price,
                unit_cost: product.unit_cost,
                quantity: 1,
                contains_pork: product.contains_pork || false,
                is_vegan: product.is_vegan || false,
                is_halal: product.is_halal || false,
            }]);
        }
        toast.success(`${product.title} ajouté`);
    };

    const updateQuantity = (productId, newQty) => {
        const qty = Math.max(1, parseInt(newQty) || 1);
        setQuoteItems(quoteItems.map(item => 
            item.product_id === productId ? { ...item, quantity: qty } : item
        ));
    };

    const removeItem = (productId) => {
        setQuoteItems(quoteItems.filter(item => item.product_id !== productId));
    };

    // Performance calculations
    const performance = useMemo(() => {
        const totalSale = quoteItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
        const totalCost = quoteItems.reduce((sum, item) => sum + (item.unit_cost * item.quantity), 0);
        const grossMargin = totalSale - totalCost;
        const marginRate = totalSale > 0 ? (grossMargin / totalSale) * 100 : 0;

        return {
            totalSale: Math.round(totalSale * 100) / 100,
            totalCost: Math.round(totalCost * 100) / 100,
            grossMargin: Math.round(grossMargin * 100) / 100,
            marginRate: Math.round(marginRate * 10) / 10,
        };
    }, [quoteItems]);

    const handleSave = async () => {
        if (!clientInfo.client_name || !clientInfo.event_type || !clientInfo.event_date) {
            toast.error('Veuillez remplir les informations client');
            return;
        }

        if (quoteItems.length === 0) {
            toast.error('Ajoutez au moins un produit');
            return;
        }

        setSaving(true);
        try {
            const data = {
                client_name: clientInfo.client_name,
                client_address: clientInfo.client_address,
                event_type: clientInfo.event_type,
                event_date: clientInfo.event_date.toISOString().split('T')[0],
                items: quoteItems,
            };

            if (isEditing) {
                await quotesApi.update(quoteId, data);
                toast.success('Devis modifié');
            } else {
                await quotesApi.create(data);
                toast.success('Devis créé');
            }
            navigate('/documents');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Get badge component for item
    const getBadges = (item) => {
        const badges = [];
        if (item.is_halal) badges.push({ type: 'H', color: 'bg-emerald-600', title: 'Halal' });
        if (item.is_vegan) badges.push({ type: 'V', color: 'bg-green-500', title: 'Vegan' });
        if (item.contains_pork) badges.push({ type: 'P', color: 'bg-red-500', title: 'Porc' });
        return badges;
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div className="stat-card h-48 skeleton" />
                        <div className="stat-card h-64 skeleton" />
                    </div>
                    <div className="stat-card h-96 skeleton" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="quote-editor-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="text-[#94a3b8] hover:text-[#f8fafc]"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-[#f8fafc]">
                            {isEditing ? 'Modifier le devis' : 'Nouveau devis'}
                        </h1>
                        <p className="text-[#94a3b8] mt-1">Composez votre offre</p>
                    </div>
                </div>
                <Button 
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-quote-btn"
                    className="bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold"
                >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
            </div>

            <div className="space-y-5">
                {/* Compact Client Band */}
                <div className="stat-card !p-0 overflow-hidden">
                    <div className="grid grid-cols-1 xl:grid-cols-[110px_1fr] border-b border-[#1e293b]">
                        <div className="px-6 py-5 text-[#94a3b8] text-lg font-semibold tracking-wide border-b xl:border-b-0 xl:border-r border-[#1e293b]">
                            DEVIS
                        </div>
                        <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                                <div>
                                    <Label className="text-[#64748b] uppercase tracking-wide text-xs">Client *</Label>
                                    <div className="relative mt-1.5">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                        <Input
                                            data-testid="client-name-input"
                                            value={clientInfo.client_name}
                                            onChange={(e) => setClientInfo({ ...clientInfo, client_name: e.target.value })}
                                            placeholder="Nom du client"
                                            className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] h-10"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[#64748b] uppercase tracking-wide text-xs">Adresse</Label>
                                    <div className="relative mt-1.5">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                        <Input
                                            data-testid="client-address-input"
                                            value={clientInfo.client_address}
                                            onChange={(e) => setClientInfo({ ...clientInfo, client_address: e.target.value })}
                                            placeholder="Adresse"
                                            className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] h-10"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[#64748b] uppercase tracking-wide text-xs">Prestation *</Label>
                                    <div className="relative mt-1.5">
                                        <PartyPopper className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                        <Input
                                            data-testid="event-type-input"
                                            value={clientInfo.event_type}
                                            onChange={(e) => setClientInfo({ ...clientInfo, event_type: e.target.value })}
                                            placeholder="Ex: Mariage, Buffet..."
                                            className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] h-10"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[#64748b] uppercase tracking-wide text-xs">Date événement *</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                data-testid="event-date-btn"
                                                className="w-full mt-1.5 justify-start text-left font-normal bg-[#020617] border-[#1e293b] text-[#f8fafc] hover:bg-[#1e293b] h-10"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4 text-[#475569]" />
                                                {clientInfo.event_date ? (
                                                    format(clientInfo.event_date, 'PPP', { locale: fr })
                                                ) : (
                                                    <span className="text-[#475569]">Sélectionner</span>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-[#0f172a] border-[#1e293b]" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={clientInfo.event_date}
                                                onSelect={(date) => setClientInfo({ ...clientInfo, event_date: date })}
                                                locale={fr}
                                                className="bg-[#0f172a] text-[#f8fafc]"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Workspace */}
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
                    {/* Catalogue - Wider */}
                    <div className="stat-card">
                        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
                            <h2 className="text-2xl font-semibold text-[#f8fafc] tracking-wide">
                                Catalogue <span className="text-[#64748b] text-xl">- {groupedProducts.length} produits</span>
                            </h2>
                            <div className="relative w-full xl:w-80">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                <Input
                                    data-testid="search-catalog-input"
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] h-11"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4">
                            {CATEGORIES.map((cat) => (
                                <button
                                    key={cat.value}
                                    onClick={() => setActiveCategory(cat.value)}
                                    className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                                        activeCategory === cat.value
                                            ? 'bg-[#d4af37]/20 border-[#d4af37]/40 text-[#d4af37]'
                                            : 'border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]'
                                    }`}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 max-h-[calc(100vh-22rem)] overflow-y-auto pr-2">
                            {groupedProducts.map((group) => (
                                <GroupedProductCard
                                    key={group.title}
                                    title={group.title}
                                    variants={group.variants}
                                    onSelectVariant={addProduct}
                                />
                            ))}
                            {groupedProducts.length === 0 && (
                                <div className="md:col-span-2 2xl:col-span-3 text-center py-10">
                                    <Package className="w-10 h-10 text-[#334155] mx-auto mb-2" />
                                    <p className="text-[#64748b]">Aucun produit trouvé</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Indicators + Items */}
                    <div className="quote-preview space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="perf-indicator">
                                <div className="flex items-center gap-2 mb-1">
                                    <Euro className="w-4 h-4 text-[#d4af37]" />
                                    <span className="text-[#94a3b8] text-xs uppercase tracking-wide">Total vente</span>
                                </div>
                                <p className="text-4xl font-bold text-[#d4af37] mono leading-none" data-testid="total-sale">
                                    {formatCurrency(performance.totalSale)}
                                </p>
                            </div>
                            <div className="perf-indicator">
                                <div className="flex items-center gap-2 mb-1">
                                    <TrendingUp className="w-4 h-4 text-[#60a5fa]" />
                                    <span className="text-[#94a3b8] text-xs uppercase tracking-wide">Coût revient</span>
                                </div>
                                <p className="text-4xl font-bold text-[#60a5fa] mono leading-none" data-testid="total-cost">
                                    {formatCurrency(performance.totalCost)}
                                </p>
                            </div>
                            <div className="perf-indicator">
                                <div className="flex items-center gap-2 mb-1">
                                    <Euro className="w-4 h-4 text-green-400" />
                                    <span className="text-[#94a3b8] text-xs uppercase tracking-wide">Marge brute</span>
                                </div>
                                <p className={`text-4xl font-bold mono leading-none ${performance.grossMargin >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="gross-margin">
                                    {formatCurrency(performance.grossMargin)}
                                </p>
                            </div>
                            <div className="perf-indicator">
                                <div className="flex items-center gap-2 mb-1">
                                    <Percent className="w-4 h-4 text-red-400" />
                                    <span className="text-[#94a3b8] text-xs uppercase tracking-wide">Taux marge</span>
                                </div>
                                <p className={`text-4xl font-bold mono leading-none ${performance.marginRate >= 30 ? 'text-green-400' : performance.marginRate >= 15 ? 'text-orange-400' : 'text-red-400'}`} data-testid="margin-rate">
                                    {performance.marginRate}%
                                </p>
                            </div>
                        </div>

                        <div className="stat-card">
                            <h2 className="text-xl font-semibold text-[#f8fafc] mb-4">
                                Articles ({quoteItems.length})
                            </h2>
                            
                            {quoteItems.length > 0 ? (
                                <div className="space-y-3 max-h-[calc(100vh-28rem)] overflow-y-auto pr-1">
                                    {quoteItems.map((item) => (
                                        <div
                                            key={item.product_id}
                                            className="p-3 rounded-lg bg-[#020617]/50 border border-[#1e293b]"
                                            data-testid={`quote-item-${item.product_id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[#f8fafc] font-medium truncate">{item.title}</p>
                                                        {getBadges(item).map((badge, i) => (
                                                            <span 
                                                                key={i} 
                                                                className={`${badge.color} w-5 h-5 rounded flex items-center justify-center text-xs text-white font-bold`}
                                                                title={badge.title}
                                                            >
                                                                {badge.type}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <p className="text-[#64748b] text-sm mono">
                                                        {formatCurrency(item.unit_price)} × {item.quantity}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeItem(item.product_id)}
                                                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-400/10"
                                                    data-testid={`remove-${item.product_id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                        className="h-8 w-8 p-0 text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b]"
                                                        data-testid={`decrease-${item.product_id}`}
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </Button>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => updateQuantity(item.product_id, e.target.value)}
                                                        className="w-14 h-8 text-center p-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] mono"
                                                        data-testid={`quantity-input-${item.product_id}`}
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                        className="h-8 w-8 p-0 text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b]"
                                                        data-testid={`increase-${item.product_id}`}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                <p className="text-[#d4af37] font-semibold mono text-lg">
                                                    {formatCurrency(item.unit_price * item.quantity)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <Package className="w-12 h-12 text-[#334155] mx-auto mb-3" />
                                    <p className="text-[#64748b]">Aucun article</p>
                                    <p className="text-[#475569] text-sm mt-1">Cliquez sur un produit pour l'ajouter</p>
                                </div>
                            )}

                            {quoteItems.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-[#1e293b] flex justify-between items-center">
                                    <span className="text-[#f8fafc] font-semibold">TOTAL</span>
                                    <span className="text-2xl font-bold text-[#d4af37] mono">
                                        {formatCurrency(performance.totalSale)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
